import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { Coordinates } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { styled, type Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { restrictToWindowEdgesWithPadding } from "_shared/_views/drag-and-drop/dnd-kit-modifiers";
import Footer from "_shared/_views/footers/footer";
import Header from "_shared/_views/headers/header";
import SideNav from "_shared/_views/side-navs/side-nav";
import {
  AlertType,
  SystemNotificationBanner,
} from "_shared/_views/system-notification-banner/system-notification-banner";
import { useSystemNotificationBanner } from "_shared/_providers/banner/system-notification-banner-provider";
import AiAssistant from "ai-assistant/_views/ai-assistant";
import Company from "marketplace/companies/company";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import { addIndividual } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import React, { useEffect } from "react";
import axios from "axios";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import { Outlet, useLocation } from "react-router";
import { useSession } from "users/__session/session-context";
import { parseSystemMessageSeverity, SystemMessageSeverity } from "notifications/system-notifications/system-message-severity";
import useSystemMessagesRefresh from "_shared/_views/use-system-messages-refresh";

const Root = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
}));
const SubRoot = styled(Box)(() => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "row",
  height: "100%",
  overflow: "hidden",
  position: "relative",
  width: "100%",
}));
const MainContainer = styled("main")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(7, 0, 0, 0),
  },
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  overflow: "hidden",
  padding: theme.spacing(8, 0, 0, 0),
  width: "100%",
}));
const PageContent = styled("section")(() => ({
  flexGrow: 1,
  overflow: "hidden",
}));

export default function Main() {
  const location = useLocation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const assistantRef = React.useRef<HTMLDivElement | null>(null);
  const prevPathRef = React.useRef(location.pathname);

  const [defaultCoords, setDefaultCoords] = React.useState<Coordinates>({
    x: globalThis.innerWidth - 200,
    y: globalThis.innerHeight - 100,
  });
  const [chatBubbleCoords, setChatBubbleCoords] = React.useState<Coordinates | null>(null);
  const coords = chatBubbleCoords ?? defaultCoords;

  const [showNav, setShowNav] = React.useState(false);
  const { banners, clearBanner, dismissSystemBanner, setSystemBanners } = useSystemNotificationBanner();

  const session = useSession();
  const restrictedOnboarding = session.isRestrictedOnboarding;
  const authToken = session.authToken?.value;
  const userId = session.user?.id?.toString();
  const dispatch = useAttorneyHubDispatch();
  const { numDialogs, closeDialog } = useDialog();

  const dismissPersistedBanner = React.useCallback(async (id: string) => {
    if (authToken) {
      await axios.post(new URL(`/system-messages/${id}/dismiss`, AttorneyHubAPIService.apiBaseUrl).toString(), {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      }).catch(() => undefined);
    }
    dismissSystemBanner(id);
  }, [authToken, dismissSystemBanner]);

  const loadActiveSystemMessages = React.useCallback(() => {
    if (!userId || !authToken) return Promise.resolve();
    const headers = { Authorization: `Bearer ${authToken}` };
    return axios.get(new URL("/system-messages/active", AttorneyHubAPIService.apiBaseUrl).toString(), { headers })
      .then(({ data }) => {
        const active = (data as Array<{ id: string; message: string; severity: unknown }>)
          .map((item) => {
            const severity = parseSystemMessageSeverity(item.severity) ?? SystemMessageSeverity.Info;
            return {
              id: item.id,
              message: item.message,
              type: severity,
            };
          });
        setSystemBanners(active);
      })
      .catch(() => undefined);
  }, [authToken, setSystemBanners, userId]);

  useSystemMessagesRefresh(userId, loadActiveSystemMessages);

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const sensors = useSensors(mouseSensor, touchSensor);

  // Close all dialogs when the route changes
  useEffect(() => {
    const prevPath = prevPathRef.current;
    const nextPath = location.pathname;

    if (!location.state?.exemptFromClose && prevPath !== nextPath && numDialogs > 0) {
      closeDialog();
    }

    prevPathRef.current = nextPath;
  }, [location.pathname, numDialogs, closeDialog, location.state?.exemptFromClose]);

  // On initial mount
  useEffect(() => {
    if (session.user?.individualId && session.user.id && session.user.companyId && session.user.legalEntityId) {
      const userIndividual = Individual.create({
        id: session.user.individualId,
        userId: session.user.id,
        legalEntityId: session.user.legalEntityId,
        isVisible: true,
        // This seed wins over the fetched record for the rest of the session — `addIndividual` never
        // overwrites an existing `byUserId` entry — so it has to carry signing authority too. Without
        // it every reader of the cache saw the signed-in user as unable to sign, which is what the
        // Client Representative picker gates on (issue #3786).
        hasSigningAuthority: session.user.isCompanyManager,
        profile: {
          userId: session.user.id,
          firstName: session.user.name.firstName ?? "",
          lastName: session.user.name.lastName ?? "",
        },
        company: Company.create({
          id: session.user.companyId,
          legalEntityId: session.user.legalEntityId,
          profile: CompanyProfile.create({
            name: session.user.companyName ?? "",
            categories: [],
          }),
        }),
      });
      dispatch(addIndividual(userIndividual));
    }

    const assistantElement = assistantRef.current;
    if (assistantElement) {
      const { width, height } = assistantElement.getBoundingClientRect();
      placeAssistantAt(globalThis.innerWidth - width - 16, globalThis.innerHeight - height - 16);
    }

    function handleWindowResize() {
      const bubbleEl = assistantRef.current;
      if (!bubbleEl) return;

      const { width, height } = bubbleEl.getBoundingClientRect();
      const padding = 16;
      const maxX = globalThis.innerWidth - width - padding;
      const maxY = globalThis.innerHeight - height - padding;

      // Re-calc the "default" bottom-right spot
      placeAssistantAt(maxX, maxY);

      // Clamp to client area plus padding
      setChatBubbleCoords((prev) => {
        if (!prev) return null;
        const x = Math.min(Math.max(prev.x, padding), maxX);
        const y = Math.min(Math.max(prev.y, padding), maxY);
        return prev.x === x && prev.y === y ? prev : { x, y };
      });
    }

    /**
     * Moves the assistant bubble's resting corner, and only when it actually moves.
     *
     * This effect's dependencies include identity-compared values the session rebuilds, so it re-runs
     * on renders where nothing about the window changed. Handing React a fresh coordinate object each
     * time made every one of those runs schedule another render, which re-ran the effect: an
     * unbounded loop the app shell only escaped when React aborted it with "Maximum update depth
     * exceeded".
     */
    function placeAssistantAt(x: number, y: number) {
      setDefaultCoords((prev) => (prev.x === x && prev.y === y ? prev : { x, y }));
    }

    globalThis.addEventListener("resize", handleWindowResize);
    handleWindowResize();

    return () => globalThis.removeEventListener("resize", handleWindowResize);
  }, [
    dispatch,
    session.user?.companyId,
    session.user?.companyName,
    session.user?.id,
    session.user?.individualId,
    session.user?.isCompanyManager,
    session.user?.legalEntityId,
    session.user?.name.firstName,
    session.user?.name.lastName,
  ]);

  return (
    <Root>
      <CssBaseline />
      {banners.map((banner) => (
        <SystemNotificationBanner key={banner.id ?? "outage"} message={banner.message} type={banner.type ?? AlertType.Info}
          onClose={banner.id ? () => void dismissPersistedBanner(banner.id!) : clearBanner} />
      ))}
      <SubRoot>
        <Header
          toggleNavMenuClicked={() => setShowNav(!showNav)}
          hideControls={session.openEntityAgreements.length > 0}
          restrictedOnboarding={restrictedOnboarding}
        />
        {!session.openEntityAgreements.length && <SideNav showNav={showNav} navMenuClosed={() => setShowNav(false)} />}
        <MainContainer>
          <PageContent>
            <Outlet />
            <DndContext
              sensors={sensors}
              onDragEnd={({ delta }) => {
                if (isMobile) return;

                setChatBubbleCoords((prev) => {
                  const { x, y } = prev ?? defaultCoords;
                  return {
                    x: x + delta.x,
                    y: y + delta.y,
                  };
                });
              }}
              modifiers={[restrictToWindowEdgesWithPadding(16)]}
            >
              <AiAssistant ref={assistantRef} leftPos={coords.x} topPos={coords.y} isMobile={isMobile} />
            </DndContext>
          </PageContent>
          <Footer />
        </MainContainer>
      </SubRoot>
    </Root>
  );
}
